import React, { useEffect, useRef, useState } from "react";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import {
  TextField,
  Grid,
  Button,
  MenuItem,
  IconButton,
  Divider,
  Checkbox,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormHelperText,
  FormControl,
  FormControlLabel,
  Backdrop,
  CircularProgress,
  Typography,
  Stack,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import CustomerConsent from "./consent";
import { useSelector, useDispatch } from "react-redux";
import { customerLookUp, customerRegisteration, sendCustomerRegistrationOtp, updateConcent, getVerifiedCustomer, updateProfile } from "../../../actions";
import MenuBar from "../../menubar";
import authApi from "../../../api/signup";
import {
  toPascalCase,
  allowOnlyNumbers,
  allowPincodeOrOtp,
  isValidMobileNumber,
  allowOnlyAlphabetsAndSpaces,
  isValidVehicleNumber,
  isValidBharatSeriesNumber,
  isValidAddress
} from "../../../utils";
import { useParams, useSearchParams } from 'react-router-dom';
import OtpModal from "../../../components/common/otpVerificationModal";
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { radix64 } from '../../../utils/radix'

const useStyles = makeStyles({
  customerRegistrationCard: {
    minHeight: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "120px 15px 20px",
  },
  textField: {
    margin: 0,
    padding: 0,
    width: "100%",
  },
  registrationFormMain: {
    marginTop: 32,
  },
  consent: {
    marginTop: 16,
    display: "flex",
    alignItems: "center",
  },
  registerButton: {
    display: "flex",
    justifyContent: "center",
    marginTop: 20,
  },
  vehicleSection: {
    display: "flex",
    alignItems: "center",
  },
  consentLink: {
    cursor: "pointer",
    paddingLeft: 5,
    color: "red",
  },
  consentError: {
    marginLeft: 20,
    marginTop: -10,
  },
  thankyou: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    color: "rgb(46, 125, 50)",
    fontWeight: "bold",
  },
  CheckIcon: {
    fontSize: 60,
    marginBottom: 10,
  },
});

const INITIAL_ERROR = {
  customerName: "",
  mobile: "",
  pincode: "",
  consentAccepted: "",
  vehicleDetails: [],
};

const INITIAL_STATE = {
  customerName: "",
  mobile: "",
  address: "",
  vehicleDetails: [
    {
      vehicleType: "",
      registration_no: "",
    },
  ],
  pincode: "",
  city: "",
  state: "",
  consentAccepted: false,
};

const SignUp = ({ authPostRegister = true }) => {
  const classes = useStyles();
  const fullState = useSelector((state) => state.customerRegisteration);
  const dispatch = useDispatch();
  const [customer, setCustomer] = useState(INITIAL_STATE);
  const [error, setError] = useState(INITIAL_ERROR);
  const [open, setOpen] = useState({
    consentAgree: false,
    otp: false
  });
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isConcented, setIsConcented] = useState(false);
  const [otp, setOtp] = useState('');
  const { unique_id } = useParams();

  const [searchParams] = useSearchParams();
  const utmSource = searchParams.get('utm_source') || 'Organic';

  const roid = radix64.decode(searchParams.get('roid') || '').toString();


  const handleClickOpen = (type) => {
    setOpen(prev => ({ ...prev, [type]: true }));
  };

  const handleClose = (type) => {
    setOpen(prev => ({ ...prev, [type]: false }));
  };

  const consentAgree = () => {
    handleClose('consentAgree')
    setCustomer((prevFormData) => ({
      ...prevFormData,
      consentAccepted: true,
    }));
    setError((prevErrorData) => ({
      ...prevErrorData,
      consentAccepted: "",
    }));
  };

  const descriptionElementRef = useRef(null);

  useEffect(() => {
    if (open.consentAgree) {
      const { current: descriptionElement } = descriptionElementRef;
      if (descriptionElement !== null) {
        descriptionElement.focus();
      }
    }
  }, [open.consentAgree]);

  const registerCustomer = () => {
    let errorObj = Object.assign({}, INITIAL_ERROR);

    // Validate Customer Name
    if ('customerName' in customer) {
      if (!customer.customerName) {
        errorObj.customerName = "Customer Name is Required.";
      }
      else if (!/^[A-Za-z\s]+$/.test(customer.customerName)) {
        errorObj.customerName = "Name must contain only alphabets.";
      }
      else {
        errorObj.customerName = "";
      }
    }

    // Validate Mobile Number
    if ('mobile' in customer) {
      if (!customer.mobile) {
        errorObj.mobile = "Mobile number is required.";
      } else if (!isValidMobileNumber(customer.mobile)) {
        errorObj.mobile = "Invalid mobile number.";
      } else {
        errorObj.mobile = "";
      }
    }

    // Validate Vehicle Details
    if ('vehicleDetails' in customer && Array.isArray(customer.vehicleDetails)) {
      errorObj.vehicleDetails = [];

      for (let i = 0; i < customer.vehicleDetails.length; i++) {
        const vehicle = customer.vehicleDetails[i];
        const vehicleErrors = {};

        if (!vehicle.vehicleType) {
          vehicleErrors.vehicleType = "Vehicle type is required.";
        }

        if (!vehicle.registration_no) {
          vehicleErrors.registration_no = "Vehicle number is required.";
        } else if (!isValidVehicleNumber(vehicle.registration_no) && !isValidBharatSeriesNumber(vehicle.registration_no)) {
          vehicleErrors.registration_no = "Invalid vehicle number.";
        }

        if (Object.keys(vehicleErrors).length > 0) {
          errorObj.vehicleDetails[i] = vehicleErrors;
        } else {
          errorObj.vehicleDetails[i] = "";
        }
      }
    }

    // Validate Pincode
    if ('pincode' in customer) {
      if (!customer.pincode) {
        errorObj.pincode = "Pincode is required.";
      } else if (customer.pincode.length !== 6) {
        errorObj.pincode = "Invalid pincode number.";
      } else {
        errorObj.pincode = "";
      }
    }
    // Validate Consent
    if ('consentAccepted' in customer) {
      if (!customer.consentAccepted) {
        errorObj.consentAccepted = "Please accept terms and conditions.";
      } else {
        errorObj.consentAccepted = "";
      }
    }

    // Check for Errors
    const hasError = !Object.values(errorObj).every(
      (x) => x === "" || (Array.isArray(x) && x.every((v) => v === ""))
    );

    // Handle Errors or Proceed
    if (hasError) {
      setError(errorObj);
      return;
    } else {
      if (fullState?.isOtpEnabled && authPostRegister) {
        handleClickOpen('otp')
      } else {
        verifiedRegister(authPostRegister ? null : otp)
      }
    }
  };

  const updateCustomerConcent = () => {
    if (fullState?.isOtpEnabled) {
      handleClickOpen('otp')
    } else {
      dispatch(updateConcent({ concent: true, mobile_no: customer.mobile }, ()=>handleClose('otp'), undefined));
    }

  }

  const verifiedRegister = (otp) => {
    if (fullState?.userExists && !fullState?.userConcent && authPostRegister) {
      dispatch(
        updateConcent(
          {
            concent: true,
            mobile_no: customer.mobile,
            ...(fullState.isOtpEnabled ? { otp } : {})
          },
          ()=>handleClose('otp'),
          undefined)
      );
    }
    else {
      const payload = {
        customer_name: customer?.customerName?.trim()?.replace(/\s{2,}/g, ' '),
        mobile_no: customer.mobile,
        email_id: "",
        address: customer.address,
        city: customer.city,
        state: customer.state,
        pin_code: customer.pincode,
        customer_consent: customer.consentAccepted,
        vehicles: customer.vehicleDetails,
        registration_type: utmSource,
        unique_id,
        ...(roid & roid?.length ? { roid } : {}),
        ...(fullState.isOtpEnabled ? { otp } : {}),
        verification: authPostRegister ? true : false
      };
      dispatch(customerRegisteration(payload, ()=>handleClose('otp'), undefined));
    }
  }


  const verifyOtpAndRegister = async (otp) => {
    if (!authPostRegister) {
      // dispatch(customerLookUp(customer.mobile, unique_id, authPostRegister))
      const [customerData] = await Promise.all([dispatch(getVerifiedCustomer(customer.mobile, otp))]);
      if (customerData?.validOtp) {
        if (customerData?.data?.mobile) {
          setCustomer(customerData?.data);
        }
        if(customerData?.data?.consentAccepted){
          setIsConcented(true);
        }
        setIsOtpVerified(true);
        setOtp(otp);
        handleClose("otp");
      }
      return;
    }
    else {
      verifiedRegister(otp);
    }
  }

  const addVehicle = () => {
    setCustomer((prevFormData) => ({
      ...prevFormData,
      vehicleDetails: [
        ...prevFormData.vehicleDetails,
        {
          vehicleType: "",
          registration_no: "",
        },
      ],
    }));
  };

  const removeVehicle = (index) => {
    const arr = [...customer.vehicleDetails];
    if (arr.length > 1) {
      arr.splice(index, 1);
      setCustomer((prevFormData) => ({
        ...prevFormData,
        vehicleDetails: arr,
      }));
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setError((prevErrorData) => ({
      ...prevErrorData,
      [name]: "",
    }));

    if(!value?.length && ["customerName","pincode"].includes(name)){
      setError((prevErrorData) => ({
      ...prevErrorData,
      [name]: `${name == "customerName"? "Customer Name" : "Pincode"} required`,
    }));
    }

    if (name === "customerName" || name === "city" || name === "state") {
      if (!value?.length || allowOnlyAlphabetsAndSpaces(value)) {
        setCustomer((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    } else if (name === "address"){
      if(!value?.length || isValidAddress(value)){
        setCustomer((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    } else if (name === "mobile") {
      if (allowOnlyNumbers(value)) {
        setCustomer((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    } else if (name === "pincode") {
      if (allowPincodeOrOtp(value)) {
        if (value.length === 6) {
          authApi
            .getStateByPincode(value)
            .then((userResponse) => {
              setCustomer((prevFormData) => ({
                ...prevFormData,
                city: userResponse?.data?.city ? userResponse?.data?.city : "",
                state: userResponse?.data?.state
                  ? userResponse?.data?.state
                  : "",
              }));
            })
            .catch((error) => {
              console.log("error=>", error);
            });
        }
        setCustomer((prevFormData) => ({
          ...prevFormData,
          [name]: value,
        }));
      }
    } else {
      setCustomer((prevFormData) => ({
        ...prevFormData,
        [name]: value,
      }));
    }
  };

  const updateFieldChanged = (index) => (e) => {
    let newArr = [...customer.vehicleDetails];
    let errArr = [...error.vehicleDetails];

    let value = e.target.value;

    // Trim spaces only for registration_no field
    if (e.target.name === "registration_no") {
      value = value.replace(/[^a-zA-Z0-9]/g, "")?.toUpperCase(); // Removes all spaces
      const isValid =
        isValidVehicleNumber(value) || isValidBharatSeriesNumber(value);
      if (!isValid && value !== "") {
        errArr[index] = "Invalid registration number.";
      } else if(value == ""){
        errArr[index] = "Vehicle Number is Required."
      } else {
      errArr[index] = "";
}
    }

    newArr[index][e.target.name] = value;

    // if (errArr.length && errArr[index]) {
    //   errArr[index] = "";
    // }

    setCustomer((prevFormData) => ({
      ...prevFormData,
      vehicleDetails: newArr,
    }));

    setError((prevErrorData) => ({
      ...prevErrorData,
      vehicleDetails: errArr,
    }));
  };


  const handleUserCheckClick = () => {
    let errorObj = Object.assign({}, INITIAL_ERROR);
    const {
      mobile,
    } = customer;

    if (!mobile) {
      errorObj.mobile = "Mobile number is required.";
      setError(errorObj);
      return;
    } else if (!isValidMobileNumber(mobile)) {
      errorObj.mobile = "Invalid Mobile Number";
      setError(errorObj);
      return;
    }

    dispatch(customerLookUp(mobile, unique_id))
  }

  const handleSendOtp = () => {
    const payload = {
      userId: customer.mobile,
      action: "CUSTOMER_REGISTRATION",
    };

    dispatch(sendCustomerRegistrationOtp(payload, () => { }, () => handleClose('otp')));
    return true
  };

  return (
    <>
      <MenuBar
        title={"Customer Registration"}
      />
      <Box className={classes.customerRegistrationCard}>
        <Card elevation={2} sx={{ maxWidth: 900, minWidth: '50%' }}>
          {fullState?.registerationSuccess || (!authPostRegister && (!isOtpVerified && open.otp)||isConcented) ? null : (<CardHeader
            sx={{ mb: -2 }}
            title={"Customer Registration"}
          />)}
          {
              (authPostRegister && fullState?.isUserCheckCompleted && customer?.mobile) || (!authPostRegister && isOtpVerified) ?
              fullState?.registerationSuccess || (!authPostRegister && isOtpVerified && isConcented) ?
                <Box sx={{
                  p: 2
                }} className={classes.thankyou}>
                  <Box className={classes.CheckIcon}>
                    <CheckCircleOutlineIcon fontSize="inherit" />
                  </Box>
                  <Box> {fullState?.message || "You're already part of our network."}</Box>
                </Box>
                :
                fullState?.userExists && authPostRegister ?
                  !fullState?.userConcent ?
                    (<CardContent
                    >
                      <Divider />
                      <>
                        <Grid
                          container
                          columnSpacing={2}
                          className={classes.registrationFormMain}
                        >
                          <Grid item xs={12} md={6}>
                            <TextField
                              required
                              id="standard-basic"
                              label="Mobile Number"
                              disabled={true}
                              className={classes.textField}
                              margin="normal"
                              name="mobile"
                              type="tel"
                              inputProps={{
                                maxLength: 10,
                              }}
                              value={customer.mobile}
                              error={!!error.mobile}
                              helperText={!!error.mobile ? error.mobile : ""}
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Box className={classes.consent}>
                              <FormControl error={!!error} component="fieldset">
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={customer.consentAccepted}
                                      onChange={() =>
                                        customer.consentAccepted
                                          ? setCustomer((prevFormData) => ({
                                            ...prevFormData,
                                            consentAccepted:
                                              !customer.consentAccepted,
                                          }))
                                          : handleClickOpen('consentAgree')
                                      }
                                      name="consentAccepted"
                                      color="primary"
                                    />
                                  }
                                  label={
                                    <>
                                      I agree to the
                                      <span
                                        onClick={() => handleClickOpen('consentAgree')}
                                        className={classes.consentLink}
                                        color="primary"
                                      >
                                        Terms and Conditions
                                      </span>
                                    </>
                                  }
                                />
                                {error.consentAccepted && (
                                  <Box className={classes.consentError}>
                                    <FormHelperText>
                                      {error.consentAccepted}
                                    </FormHelperText>
                                  </Box>
                                )}
                              </FormControl>
                            </Box>
                          </Grid>

                        </Grid>
                        <Box className={classes.registerButton}>
                          <Button
                            variant="contained"
                            onClick={updateCustomerConcent}
                            disabled={fullState.initiated}
                          >
                            Register
                          </Button>
                        </Box>
                      </>
                    </CardContent>
                    )
                    :
                    <Stack direction={'row'} gap={2} alignItems={'center'} padding={2}>
                      <HighlightOffIcon color="error" />
                      <Typography color={'error'} variant="h6">
                        {fullState?.message}
                      </Typography>
                    </Stack>
                  :
                  (<CardContent
                  >
                    <Divider />
                    <>
                      <Grid
                        container
                        columnSpacing={2}
                        className={classes.registrationFormMain}
                      >
                        <Grid item xs={12} md={6}>
                          <TextField
                            required
                            id="standard-basic"
                            label="Customer Name"
                            inputProps={{
                              maxLength: 50,
                            }}
                            className={classes.textField}
                            margin="normal"
                            name="customerName"
                            onChange={handleChange}
                            value={customer.customerName}
                            error={!!error.customerName}
                            helperText={
                              !!error.customerName ? "Customer Name Required." : customer.customerName.length >= 50 ? "Maximum character length 50" : undefined
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            required
                            id="standard-basic"
                            label="Mobile Number"
                            disabled={(fullState?.isUserCheckCompleted && customer?.mobile) || !authPostRegister}
                            className={classes.textField}
                            margin="normal"
                            name="mobile"
                            type="tel"
                            inputProps={{
                              maxLength: 10,
                            }}
                            onChange={handleChange}
                            value={customer.mobile}
                            error={!!error.mobile}
                            helperText={!!error.mobile ? error.mobile : ""}
                          />
                        </Grid>
                        {customer?.vehicleDetails?.map((vehicle, idx) => (
                          <Grid
                            item
                            container
                            xs={12}
                            spacing={2}
                            className={classes.vehicleSection}
                            key={idx}
                          >
                            {/* Vehicle Type Field */}
                            <Grid item xs={12} sm={5} md={6}>
                              <TextField
                                id={`vehicle-type-${idx}`}
                                select
                                required
                                label="Vehicle Type"
                                className={classes.textField}
                                value={vehicle.vehicleType}
                                name="vehicleType"
                                margin="normal"
                                onChange={updateFieldChanged(idx)}
                                error={Boolean(error?.vehicleDetails?.[idx]?.vehicleType)}
                                helperText={error?.vehicleDetails?.[idx]?.vehicleType || ""}
                              >
                                <MenuItem value={"twoWheeler"}>Two Wheeler</MenuItem>
                                <MenuItem value={"threeWheeler"}>Three Wheeler</MenuItem>
                                <MenuItem value={"fourWheeler"}>Four Wheeler</MenuItem>
                                <MenuItem value={"lcv"}>Light Commercial Vehicle</MenuItem>
                                <MenuItem value={"hcv"}>Heavy Commercial Vehicle</MenuItem>
                              </TextField>
                            </Grid>

                            {/* Vehicle Number and Add/Remove Icon in the same line */}
                            <Grid item container xs={12} sm={7} md={6} alignItems="center" spacing={2}>
                              {/* Vehicle Number Field */}
                              <Grid item xs={10} sm={8} md={10}>
                                <TextField
                                  required
                                  id={`vehicle-number-${idx}`}
                                  label="Vehicle Number"
                                  placeholder="HR01AB2233"
                                  className={classes.textField}
                                  margin="normal"
                                  value={vehicle.registration_no?.toUpperCase()}
                                  name="registration_no"
                                  onChange={updateFieldChanged(idx)}
                                  inputProps={{
                                    maxLength: 10,
                                  }}
                                  error={Boolean(error?.vehicleDetails?.[idx]?.registration_no)}
                                  helperText={error?.vehicleDetails?.[idx]?.registration_no || ""}
                                />
                              </Grid>

                              {/* Add/Remove Icon */}
                              <Grid item xs={2} sm={4} md={2}>
                                {customer.vehicleDetails.length === 1 ?
                                (
                                  <IconButton onClick={addVehicle}>
                                    <AddIcon />
                                  </IconButton>
                                ) : customer?.vehicleDetails?.length < 5 ? (
                                  <>
                                  <IconButton onClick={() => removeVehicle(idx)}>
                                    <RemoveIcon />
                                  </IconButton>
                                  <IconButton onClick={addVehicle}>
                                    <AddIcon />
                                  </IconButton>
                                  </>
                                ) : customer?.vehicleDetails?.length >= 5 && (
                                  <>
                                  <IconButton onClick={() => removeVehicle(idx)}>
                                    <RemoveIcon />
                                  </IconButton>
                                  </>
                                )}
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="standard-basic"
                            label="House No/ Street Address"
                            multiline
                            maxRows={2}
                            className={`${classes.textField} ${classes.textArea}`}
                            margin="normal"
                            name="address"
                            inputProps={{
                              maxLength: 300,
                            }}
                            helperText={customer.address?.length >= 300 ? "Maximum character length 300" : undefined}
                            onChange={handleChange}
                            value={customer.address}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            required
                            id="standard-basic"
                            label="Pincode"
                            className={classes.textField}
                            margin="normal"
                            name="pincode"
                            type="tel"
                            inputProps={{
                              maxLength: 6,
                            }}
                            onChange={handleChange}
                            value={customer.pincode}
                            error={!!error.pincode}
                            helperText={!!error.pincode ? error.pincode : ""}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="standard-basic"
                            label="City"
                            className={classes.textField}
                            margin="normal"
                            name="city"
                            inputProps={{
                              maxLength: 100,
                            }}
                            helperText={customer.city?.length >= 100 ? "Maximum character length 60" : undefined}
                            onChange={handleChange}
                            value={toPascalCase(customer?.city)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            id="standard-basic"
                            label="State"
                            className={classes.textField}
                            margin="normal"
                            name="state"
                            inputProps={{
                              maxLength: 100,
                            }}
                            helperText={customer.state?.length >= 100 ? "Maximum character length 100" : undefined}
                            onChange={handleChange}
                            value={toPascalCase(customer?.state)}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <Box className={classes.consent}>
                            <FormControl error={!!error} component="fieldset">
                              <FormControlLabel
                                control={
                                  <Checkbox
                                    checked={customer.consentAccepted}
                                    onChange={() =>
                                      customer.consentAccepted
                                        ? setCustomer((prevFormData) => ({
                                          ...prevFormData,
                                          consentAccepted:
                                            !customer.consentAccepted,
                                        }))
                                        : handleClickOpen('consentAgree')
                                    }
                                    name="consentAccepted"
                                    color="primary"
                                  />
                                }
                                label={
                                  <>
                                    I agree to the
                                    <span
                                      onClick={() => handleClickOpen('consentAgree')}
                                      className={classes.consentLink}
                                      color="primary"
                                    >
                                      Terms and Conditions
                                    </span>
                                  </>
                                }
                              />
                              {error.consentAccepted && (
                                <Box className={classes.consentError}>
                                  <FormHelperText>
                                    {error.consentAccepted}
                                  </FormHelperText>
                                </Box>
                              )}
                            </FormControl>
                          </Box>
                        </Grid>

                      </Grid>
                      <Box className={classes.registerButton}>
                        <Button
                          variant="contained"
                          onClick={registerCustomer}
                          disabled={fullState.initiated}
                        >
                          Register
                        </Button>
                      </Box>
                    </>

                  </CardContent>)
              :
              (
                !open.otp && (
                  <CardContent>
                    <Stack gap={5}>
                      <TextField
                        id="standard-basic"
                        label="Mobile Number"
                        placeholder="Enter your 10 digits mobile number"
                        className={classes.textField}
                        name="mobile"
                        type="tel"
                        inputProps={{
                          maxLength: 10,
                        }}
                        onChange={handleChange}
                        value={customer.mobile}
                        error={!!error.mobile}
                        helperText={!!error.mobile ? error.mobile : ""}
                      />

                      <Button
                        variant="contained"
                        onClick={() => {
                          if (authPostRegister) {
                            handleUserCheckClick();
                          } else {
                            if (!customer.mobile) {
                              setError({ ...error, mobile: "Mobile number is required." });
                              return;
                            }
                            if (!isValidMobileNumber(customer.mobile)) {
                              setError({ ...error, mobile: "Invalid Mobile Number" });
                              return;
                            }
                            handleClickOpen("otp");
                          }
                        }}
                      >
                        Proceed
                      </Button>

                    </Stack>

                  </CardContent>
                )
              )
          }
        </Card>
      </Box>
      <CustomerConsent
        open={open.consentAgree}
        handleClose={() => handleClose('consentAgree')}
        consentAgree={consentAgree}
        data={''}
      />
      <Backdrop
        className={classes.backdrop}
        open={
          fullState.initiated
        }
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      {open.otp ? <OtpModal
        open={open.otp}
        onClose={() => fullState?.initiated ? undefined : handleClose('otp')}
        mobileNo={customer.mobile}
        onSendOtp={handleSendOtp}
        onSubmitOtp={verifyOtpAndRegister}
        submitButtonLabel={authPostRegister ? "Verify & Submit" : "Verify OTP"}
        disableForm={fullState?.initiated}
      /> : null}

    </>
  );
};

export default SignUp